import { useState } from "react";
import { Link } from "react-router";
import { api } from "../../shared/api/client";
import type { Announcement, MinistriesOverview } from "../../shared/api/types";
import { useBadges } from "../../shared/badges";
import { useAsync } from "../../shared/ui/useAsync";
import { useMe } from "../session/SessionProvider";
import {
  badgeFor,
  describeItems,
  HINTS,
  HUBS,
  hubItems,
  Icon,
  useNavGroups,
  type NavItem,
} from "./nav";

const SEEN_KEY = "keora.avisos.vistos";

function seenIds(): string[] {
  try {
    return JSON.parse(localStorage.getItem(SEEN_KEY) ?? "[]") as string[];
  } catch {
    return [];
  }
}

/** El aviso más reciente que aún no cierras; si no hay ninguno, no aparece nada. */
export function AnnouncementBanner({ churchId }: { churchId: string }) {
  const list = useAsync(
    (signal) =>
      api
        .get<{
          items: Announcement[];
        }>(`/communication/churches/${churchId}/announcements`, signal)
        .then((r) => r.items),
    [churchId],
  );
  const [seen, setSeen] = useState(seenIds);
  if (list.status !== "ready") return null;
  const pending = list.data.filter((a) => !seen.includes(a.id));
  const current = pending[0];
  if (!current) return null;
  function close() {
    const next = [...seen, current!.id].slice(-50);
    setSeen(next);
    try {
      localStorage.setItem(SEEN_KEY, JSON.stringify(next));
    } catch {
      // Sin almacenamiento: se oculta solo en esta visita.
    }
  }
  const more = pending.length - 1;
  const date = new Date(current.publishedAt).toLocaleDateString("es", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  return (
    <aside className="notice-banner" aria-label="Aviso de la iglesia">
      <header className="notice-banner__head">
        <span className="notice-banner__icon" aria-hidden="true">
          <Icon name="megaphone" />
        </span>
        <span className="notice-banner__eyebrow">
          Aviso de la iglesia
          <span className="notice-banner__date">{date}</span>
        </span>
      </header>
      <h2 className="notice-banner__title">{current.title}</h2>
      <p className="notice-banner__body">{current.body}</p>
      <footer className="notice-banner__foot">
        <span className="small muted">
          {current.publishedBy.name}
          {more > 0 ? (
            <>
              {" · "}
              <Link to="/app/avisos">y {more === 1 ? "otro aviso" : `${more} avisos más`}</Link>
            </>
          ) : null}
        </span>
        <button
          type="button"
          className="btn btn--primary notice-banner__ok"
          onClick={close}
          aria-label="Cerrar aviso"
        >
          Entendido
        </button>
      </footer>
    </aside>
  );
}

function Tile({
  item,
  badge,
  hint,
}: {
  item: NavItem;
  badge?: number;
  hint?: string | null;
}) {
  return (
    <li>
      <Link to={item.to} className="hub__item">
        <span className="hub__icon">
          <Icon name={item.icon} />
          {badge ? (
            <span
              className="nav-badge nav-badge--dot"
              aria-label={`${badge} pendientes`}
            >
              {badge}
            </span>
          ) : null}
        </span>
        <span>
          <strong>{item.label}</strong>
          {hint ? <span className="small muted">{hint}</span> : null}
        </span>
      </Link>
    </li>
  );
}

/**
 * Mi iglesia del miembro: un aviso solo cuando hay uno, lo de todos los días (Culto, Raíces, Agenda,
 * Chats, Oración, Avisos, Mi vocación, Nuestra historia… y Comunidad) y sus ministerios.
 * Las solicitudes van en el botón flotante.
 */
export function MyChurchPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const groups = useNavGroups();
  const badges = useBadges(me);
  const items = hubItems(groups, "Mi iglesia");
  const community = HUBS.find((h) => h.title === "Comunidad")!;
  const communityItems = hubItems(groups, "Comunidad");
  const hasCommunity = communityItems.length > 0;
  const ministries = useAsync(
    (signal) =>
      churchId && me.can("departments.read")
        ? api
            .get<MinistriesOverview>(
              `/organization/churches/${churchId}/ministries`,
              signal,
            )
            .then((r) => r.items.filter((m) => m.mine))
        : Promise.resolve([]),
    [churchId],
  );
  const hub = HUBS.find((h) => h.title === "Mi iglesia")!;
  return (
    <div className="page">
      <header className="page__header">
        <h1>{hub.title}</h1>
        {me.activeContext ? (
          <p className="muted">{me.activeContext.churchName}</p>
        ) : null}
      </header>
      {churchId && me.can("communication.read") ? (
        <AnnouncementBanner churchId={churchId} />
      ) : null}
      <ul className="hub">
        {items.map((item) => (
          <Tile
            key={item.to}
            item={item}
            badge={badgeFor(item, badges)}
            hint={HINTS[item.to]}
          />
        ))}
        {hasCommunity ? (
          <Tile
            item={{
              to: community.to,
              label: community.title,
              short: community.short,
              icon: community.icon,
            }}
            hint={describeItems(communityItems) || community.subtitle}
          />
        ) : null}
      </ul>
      {ministries.status === "ready" && ministries.data.length > 0 ? (
        <section className="stack">
          <h2>Mis ministerios</h2>
          <ul className="hub">
            {ministries.data.map((m) => (
              <Tile
                key={m.id}
                item={{
                  to: `/app/ministerios/${m.id}`,
                  label: m.name,
                  short: m.name,
                  icon: "ministry",
                }}
                hint={m.leader ? `Líder: ${m.leader.name}` : null}
              />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
